import { Select, SelectProps } from "@mantine/core";
import { Functions, httpsCallable } from "firebase/functions";
import { useMemo } from "react";
import { getCountriesList } from "../utils";
import { MobileLookupRequest, MobileLookupResult } from "../types";

/**
 * Country dial-code picker backed by `getCountriesList()` in utils.
 *
 * Mantine's Select needs unique option values and several countries share a
 * dial code (+1, +44, +7…), so the option value is the country ISO-2 code and
 * the selected country's `dial_code` is what gets stored in `mobile_code`.
 */

export const KUWAIT_DIAL_CODE = "+965";
export const EGYPT_DIAL_CODE = "+20";

/** Preferred country when several share a dial code. */
const PREFERRED_COUNTRY_FOR_DIAL_CODE: Record<string, string> = {
    "+1": "US",
    "+7": "RU",
    "+44": "GB",
    "+61": "AU",
    "+47": "NO",
    "+212": "MA",
    "+262": "RE",
    "+290": "SH",
    "+358": "FI",
    "+590": "GP",
    "+599": "CW",
};

export type DialCodeCountry = ReturnType<typeof getCountriesList>[number];

const normalizeDialCode = (code?: string | null): string | undefined => {
    const digits = (code ?? "").replace(/[^\d]/g, "");
    return digits ? `+${digits}` : undefined;
};

/** Country entry for a dial code ("+965" -> Kuwait). Undefined when the code is unknown. */
export const countryForDialCode = (dialCode?: string | null): DialCodeCountry | undefined => {
    const code = normalizeDialCode(dialCode);
    if (!code) return undefined;
    const countries = getCountriesList();
    const preferred = PREFERRED_COUNTRY_FOR_DIAL_CODE[code];
    return countries.find((c) => c.value === preferred && c.dial_code === code)
        ?? countries.find((c) => c.dial_code === code);
};

/** Dial code for a country ISO-2 code ("EG" -> "+20"). */
export const dialCodeForCountry = (iso2?: string | null): string | undefined =>
    getCountriesList().find((c) => c.value === iso2)?.dial_code;

/** Default dial code for a segment: "+20" for KFH Egypt, "+965" for everything else (mirrors shared-helpers/src/mobileHelpers.ts). */
export const dialCodeForSegment = (segment?: string | null): string =>
    /egypt/i.test(segment ?? "") ? EGYPT_DIAL_CODE : KUWAIT_DIAL_CODE;

/**
 * Split a typed mobile into { dialCode, local }. A "+"-prefixed (or "00"-prefixed)
 * number is matched against the longest known dial code; otherwise the number is
 * treated as local digits under `fallbackDialCode`.
 */
export const splitMobileWithDialCode = (
    mobile: string,
    fallbackDialCode: string = KUWAIT_DIAL_CODE,
): { dialCode: string, local: string, matched: boolean } => {
    let normalized = (mobile || "").replace(/[\s()-]/g, "");
    if (normalized.startsWith("00")) normalized = "+" + normalized.slice(2);
    if (!normalized.startsWith("+")) {
        return { dialCode: fallbackDialCode, local: normalized, matched: false };
    }
    const codes = [...new Set(getCountriesList().map((c) => c.dial_code))]
        .sort((a, b) => b.length - a.length);
    const match = codes.find((code) => normalized.startsWith(code) && normalized.length > code.length);
    if (match) {
        return { dialCode: match, local: normalized.slice(match.length), matched: true };
    }
    return { dialCode: fallbackDialCode, local: normalized.replace(/^\+/, ""), matched: false };
};

/**
 * Cheap shape check only (digits, plausible length). Real validity comes from
 * Twilio Lookup via `lookupMobile` — no per-country rules are kept here.
 */
export const isValidLocalMobile = (local: string, _dialCode?: string): boolean => {
    const digits = (local || "").replace(/[\s()-]/g, "");
    return /^\d{4,15}$/.test(digits);
};

/**
 * Validate a number with Twilio Lookup through the `validateMobileNumber` callable.
 * Returns the lookup result; `valid` is undefined when the lookup could not run
 * (offline, Twilio outage) so callers can decide to proceed with the shape check only.
 */
export const lookupMobile = async (functions: Functions, mobile: string, dialCode?: string): Promise<MobileLookupResult> => {
    try {
        const validate = httpsCallable<MobileLookupRequest, MobileLookupResult>(functions, "validateMobileNumber");
        const result = await validate({ mobile, mobile_code: dialCode });
        return result.data ?? {};
    } catch (e: any) {
        return { error: e?.message ?? "lookup failed" };
    }
};

/** Human-readable reason for an invalid lookup. */
export const describeInvalidMobile = (result: MobileLookupResult): string => {
    const reasons = (result.validationErrors ?? []).map((code) => code.replace(/_/g, " ").toLowerCase());
    return `Twilio could not validate ${result.e164 ?? "this number"}${reasons.length ? ` (${reasons.join(", ")})` : ""}. Check the country code and the digits.`;
};

export const formatMobileWithDialCode = (mobile?: string | null, dialCode?: string | null): string => {
    if (!mobile) return "";
    if (mobile.startsWith("+")) return mobile;
    return `${normalizeDialCode(dialCode) ?? KUWAIT_DIAL_CODE} ${mobile}`;
};

interface MobileCodeSelectProps extends Omit<SelectProps, "data" | "value" | "onChange"> {
    /** Selected dial code, e.g. "+965". */
    value?: string | null;
    onChange: (dialCode: string) => void;
}

const MobileCodeSelect = ({ value, onChange, ...selectProps }: MobileCodeSelectProps) => {
    const data = useMemo(() => getCountriesList().map((c) => ({
        value: c.value,
        label: `${c.emoji} ${c.dial_code} ${c.label}`,
    })), []);

    const selectedCountry = countryForDialCode(value);

    return (
        <Select
            label="Country code"
            searchable
            allowDeselect={false}
            nothingFoundMessage="No country"
            comboboxProps={{ width: 320, position: "bottom-start" }}
            {...selectProps}
            data={data}
            value={selectedCountry?.value ?? null}
            onChange={(iso2) => {
                const dialCode = dialCodeForCountry(iso2);
                if (dialCode) onChange(dialCode);
            }}
        />
    );
};

export default MobileCodeSelect;
